import { useState } from 'react';
import { fmtDate } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { PROJECT_STATUS_META } from '../projects/types';
import MilestoneSteps, { MilestoneBadge } from './MilestoneSteps';
import { usePortal, portalThumbUrl, portalFileUrl } from './api';
import PortalQuotes from './PortalQuotes';

// Token z URL /portal/<token> (portál běží mimo router i AuthGate).
function tokenFromPath(): string {
  const m = window.location.pathname.match(/\/portal\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function PortalPage() {
  const token = tokenFromPath();
  const { data, isLoading, error } = usePortal(token);
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-4xl px-4 py-10">
        {isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}

        {error && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <h1 className="text-lg font-semibold mb-1">Odkaz není dostupný</h1>
            <p className="text-sm text-zinc-500">
              {(error as ApiError).status === 404
                ? 'Tento sdílený odkaz je neplatný nebo jeho platnost vypršela.'
                : (error as ApiError).message}
            </p>
          </div>
        )}

        {data && (
          <>
            <header className="mb-8">
              <p className="text-xs uppercase tracking-wide text-zinc-400">{data.studioName}</p>
              <div className="mt-1 flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{data.project.name}</h1>
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${PROJECT_STATUS_META[data.project.status].badge}`}>
                  {PROJECT_STATUS_META[data.project.status].label}
                </span>
              </div>
              <p className="font-mono text-sm text-zinc-500">{data.project.code}</p>
              {data.project.subtitle && <p className="text-sm text-zinc-500">{data.project.subtitle}</p>}
              {data.project.deadline && (
                <p className="mt-1 text-sm text-zinc-500">Termín: {fmtDate(data.project.deadline)}</p>
              )}
            </header>

            <section className="mb-8">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">Výroby</h2>
              {data.propItems.length === 0 ? (
                <p className="text-sm text-zinc-400">Zatím bez položek.</p>
              ) : (
                <div className="space-y-2">
                  {data.propItems.map((it) => (
                    <div key={it.id} className={`rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 ${it.milestone.current === 'cancelled' ? 'opacity-60' : ''}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium">{it.name}</div>
                          {it.description && <div className="text-xs text-zinc-500">{it.description}</div>}
                        </div>
                        <MilestoneBadge milestone={it.milestone} />
                      </div>
                      <MilestoneSteps milestone={it.milestone} />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <PortalQuotes token={token} data={data} />

            {data.photos.length > 0 && (
              <section className="mb-8">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">Galerie</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {data.photos.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setLightbox(portalFileUrl(token, p.id))}
                      className="aspect-square overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-800"
                    >
                      <img src={portalThumbUrl(token, p.id)} alt={p.caption ?? ''} loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </section>
            )}

            <footer className="mt-10 border-t border-zinc-200 dark:border-zinc-800 pt-4 text-xs text-zinc-400">
              Read-only náhled · {data.studioName} · aktualizováno {fmtDate(data.generatedAt)}
            </footer>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded shadow-xl" />
        </div>
      )}
    </div>
  );
}
